import { Fragment, useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faLongArrowDown, faLongArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Button, Card } from 'react-bootstrap';

import { SUPPORT_EMAIL } from '@constants/constants';
import useViewportSizing from '@hooks/useViewportSizing';

import OrderStatusCard from './OrderStatusCard';
import type { IOrderProcessStep } from './OrderStatusCard';

import './OrderStatusInfo.scss';

export const ORDER_PROCESS_STEPS: IOrderProcessStep[] = [
  {
    step: 'Step One',
    title: 'Ordered',
    description: (
      <>
        We've received your order and are awaiting the sample(s).
        <br />
        <br />
        If a duo or trio test was ordered, all samples must arrive before we can begin processing.
        <br />
        <br />
        If you requested a kit to be shipped to your patient, it typically arrives in 3-5 business days.
      </>
    ),
  },
  {
    step: 'Step Two',
    title: 'Lab Processing',
    description: 'We are sequencing the sample(s) and analyzing the data to generate results.',
  },
  {
    step: 'Step Three',
    title: 'Clinician Review',
    description: (
      <>
        The results are being reviewed and a clinical summary letter is being prepared.
        <br />
        <br />
        This status is applicable for proactive orders and orders where genetic counseling is requested.
      </>
    ),
  },
  {
    step: 'Completed',
    title: 'Reported',
    description: 'Results are ready and available for download.',
  },
];

export const OrderStatusInfo = () => {
  const { isDesktop } = useViewportSizing();

  const nextStepIcon = useMemo(() => (isDesktop ? faLongArrowRight : faLongArrowDown), [isDesktop]);

  return (
    <section className='border-top flex-column flex-md-row flex-lg-column gap-3 gap-lg-4 order-status-info-container'>
      <div className='w-100'>
        <p className='mb-2 fs-6 lh-xs fw-medium'>Order Process Steps</p>
        <div className='d-flex flex-column flex-lg-row gap-3 gap-lg-4 align-items-stretch order-process-steps'>
          {ORDER_PROCESS_STEPS.map((item, index) => (
            <Fragment key={index}>
              <OrderStatusCard {...item} />
              <FontAwesomeIcon
                className='order-process-step-icon'
                icon={index === ORDER_PROCESS_STEPS.length - 1 ? faCheck : nextStepIcon}
                fontSize={20}
              />
            </Fragment>
          ))}
        </div>
      </div>
      <div className='w-100 d-flex flex-column align-items-center align-items-md-start'>
        <p className='mb-2 fs-6 lh-xs fw-medium align-self-start'>Other Statuses</p>
        <div className='d-flex flex-column flex-lg-row gap-3 gap-lg-4'>
          <Card className='other-status-card'>
            <Card.Body>
              <Card.Title className='fs-6 fw-semibold'>Canceled</Card.Title>
              <Card.Text>
                <span className='align-middle fs-sm'>
                  We either received a request to cancel the order or there was an issue with the order that caused us
                  to cancel it. If you think this cancellation was a mistake, please contact us at{' '}
                </span>
                <Button
                  variant='link'
                  className='p-0 border-0 fs-sm text-decoration-none fw-medium'
                  href={`mailto:${SUPPORT_EMAIL}`}
                  target='_blank'
                >
                  {SUPPORT_EMAIL}
                </Button>
                .
              </Card.Text>
            </Card.Body>
          </Card>

          <Card className='other-status-card'>
            <Card.Body>
              <Card.Title className='fs-6 fw-semibold'>Failed</Card.Title>
              <Card.Text>
                <span className='align-middle fs-sm'>
                  There was an issue processing the sample in the lab. If an additional sample was submitted, we will
                  attempt to process that sample as well. If a new sample is required, we will contact you to discuss
                  next steps.
                </span>
              </Card.Text>
            </Card.Body>
          </Card>
        </div>
      </div>
    </section>
  );
};
